import translate, { getLangKey } from '@translate';
import { ReactNode } from 'react';
import { OrderStatusesTo, SOURCE_SYS_MYSKLADON } from 'skladon-types'
import { getTrackingUrl } from 'skladon-utils'
import { PaidActionOptionType } from 'src/components/PaymentEdit';
import { ConstantsCodesObjectType, ConstantsCodeType } from 'src/types/constantsCodes';
import { OrderProductSetWithProductType, OrderProductType, OrderType } from 'src/types/order';
import { OrderChangelogType } from 'src/types/orderDetail';
import { checkPermission } from 'src/utils/checkPermission';
import { withArchiveMode } from 'src/utils/archiveMode';
import { flattenProductCatalogFields } from 'src/utils/productCatalogTableColumns';
import {
    ProductQuantityInputValue,
    shouldRestoreProductQuantityOnBlur,
} from 'src/utils/productQuantityInput';
import { arraySameRecipientBilling, PICKUP_POINT_SERVICE_CODES, SHIPPER_FILE_TYPES } from './constants';
import {
    WaitingForCarrierOkBigIcon,
    WaitingForCarrierDisabledBigIcon,
    WaitingForCarrierErrorBigIcon,
    WaitingForCarrierWarningBigIcon,
    WaitingForCarrierSuccessBigIcon,
    DeliveredOkBigIcon,
    DeliveredDisabledBigIcon,
    DeliveredErrorBigIcon,
    DeliveredWarningBigIcon,
    DeliveredSuccessBigIcon,
    OnTheWayOkBigIcon,
    OnTheWayDisabledBigIcon,
    OnTheWayErrorBigIcon,
    OnTheWayWarningBigIcon,
    OnTheWaySuccessBigIcon,
    ReceivedOkBigIcon,
    ReceivedOkWithWarningBigIcon,
    ReceivedDisabledBigIcon,
    ReceivedErrorBigIcon,
    ReceivedWarningBigIcon,
    ReceivedSuccessBigIcon,
    ProcessingOkBigIcon,
    ProcessingDisabledBigIcon,
    ProcessingErrorBigIcon,
    ProcessingWarningBigIcon,
    ProcessingSuccessBigIcon,
    CanceledIcon,
    ProcessingOkWithWarningBigIcon,
    WaitingForCarrierOkWithWarningBigIcon,
    OnTheWayOkWithWarningBigIcon,
    DeliveredOkWithWarningBigIcon,
    DeliveredSuccessWithWarningBigIcon,
} from 'src/components/SkladonIcon/StatusIcons';
import {
    AtPickupPointDisabledBigIcon,
    AtPickupPointErrorBigIcon,
    AtPickupPointOkBigIcon,
    AtPickupPointOkWithWarningBigIcon,
    AtPickupPointSuccessBigIcon,
    AtPickupPointWarningBigIcon,
} from 'src/components/SkladonIcon/AtPickupPointStatusIcons';
import { TableLink } from 'src/components/TableLink';
import { ProductType } from 'src/types/product';
import api from '@api';
import { sumArraySimple } from 'src/utils/sumArray';
import { saveAs } from 'file-saver';
import { Packer, Document, Paragraph, TextRun } from 'docx';
import getCountryOptions from 'src/utils/countryOptions';
import moment, { Moment } from 'moment';
import { getOrderProductDisplayName, getOrderProductDisplaySku } from 'src/utils/orderProductDisplay';
import { filteringRow } from 'src/components/TableSimple/functions';
import { GridFilterType, TableColumnType, TableRowType } from 'src/types/table';

export const getAllowedStatuses = (
    orderStatusCodes: ConstantsCodeType[],
    orderStatusCodesObject: ConstantsCodesObjectType,
    orderData?: { STATUS?: string } | null
): ConstantsCodeType[] => {
    if (checkPermission('ORDER.EDITALL')) {
        return orderStatusCodes;
    }

    if (!orderData || !orderData.STATUS) {
        return [];
    }

    return [
        orderStatusCodesObject[orderData && orderData.STATUS],
        ...(OrderStatusesTo[orderData && orderData.STATUS] || [])
            .filter((statusKey) => {
                return checkPermission(`ORDER.STATUS.LIST.${statusKey.toUpperCase()}`);
            })
            .map((statusKey) => {
                return orderStatusCodesObject[statusKey];
            }),
    ];
};

export const isDisableEditPrintFiles = (order: OrderType | null, statusObject: ConstantsCodesObjectType): boolean => {
    if (checkPermission('ORDER.EDITALL')) {
        return false;
    }
    if (checkPermission('ORDER.MANAGER_EDIT')) {
        return false;
    }
    if (!checkPermission('ORDER.UPDATE') || !checkPermission('ORDER.CREATE')) {
        return true;
    }
    // if (!order?.STATUS) {
    //     return true;
    // }
    // if ((statusObject[order.STATUS]?.ORDER_INT || 0) > (statusObject.received?.ORDER_INT || 0)) {
    //     return true;
    // }
    return false;
};

export const isDisableEditAllowExpedition = (
    order: OrderType | null,
    statusObject: ConstantsCodesObjectType
): boolean => {
    if (checkPermission('ORDER.EDITALL')) {
        return false;
    }
    if (checkPermission('ORDER.MANAGER_EDIT')) {
        return false;
    }
    if (!checkPermission('ORDER.UPDATE') || !order) {
        return true;
    }

    if (!order?.STATUS) {
        return true;
    }

    if ((statusObject[order.STATUS]?.ORDER_INT || 0) > (statusObject.packed?.ORDER_INT || 0)) {
        return true;
    }
    return false;
};

export const isDisablePaidActions: any = (
    order: OrderType | null,
    statusObject: ConstantsCodesObjectType,
    newOrder: boolean
) => {
    if (newOrder) {
        return false;
    }

    if (checkPermission('ORDER.EDITALL')) {
        return false;
    }

    if (!checkPermission('ORDER.UPDATE') || !order || order.STATUS === 'preReceived') {
        return true;
    }

    if (order.ORDER_TYPE === 'b2b') {
        return !!order.TRACKING_NUMBER;
    }
    if ((statusObject[order?.STATUS || ''].ORDER_INT || 0) > (statusObject.waitingForShipment?.ORDER_INT || 0)) {
        return true;
    }
    return false;
};

export const isSameRecipientBilling = (data: OrderType): boolean => {
    if (arraySameRecipientBilling.find((row) => data[row[0]] !== data[row[1]])) {
        return false;
    }
    return true;
};

export const setSameDataRecipientBilling = (data: OrderType): OrderType => {
    const billingData: OrderType = {};
    arraySameRecipientBilling.forEach((row) => {
        billingData[row[1]] = data[row[0]];
    });
    return billingData;
};

export type OrderProductsPayload = {
    ORDER_PRODUCTS: OrderType['ORDER_PRODUCTS'];
    ORDER_PRODUCT_SETS: OrderType['ORDER_PRODUCT_SETS'];
};

export const fetchOrderProducts = async (idOrder: string | number): Promise<OrderProductsPayload> => {
    const response: { data: { data: OrderProductsPayload } } = await api(
        'get',
        withArchiveMode(`api/secure/orders/${idOrder}/products`)
    );
    return response.data.data;
};

/**
 * Ensure order has ORDER_PRODUCTS loaded (lazy detail may omit them until Products tab).
 */
export const ensureOrderHasProducts = async (
    order: OrderType,
    orderId: string | number
): Promise<OrderType> => {
    if (order.ORDER_PRODUCTS !== undefined) {
        return order;
    }
    const products = await fetchOrderProducts(orderId);
    return {
        ...order,
        ORDER_PRODUCTS: products.ORDER_PRODUCTS,
        ORDER_PRODUCT_SETS: products.ORDER_PRODUCT_SETS,
    };
};

export const fetchOrderFiles = async (idOrder: string | number): Promise<NonNullable<OrderType['FILES']>> => {
    const response: { data: { FILES: NonNullable<OrderType['FILES']> } } = await api(
        'get',
        withArchiveMode(`api/secure/orders/files/${idOrder}`)
    );
    return response.data.FILES ?? [];
};

/**
 * Ensure order has FILES loaded (lazy detail may omit them until Transport tab).
 */
export const ensureOrderHasFiles = async (
    order: OrderType,
    orderId: string | number
): Promise<OrderType> => {
    if (order.FILES !== undefined) {
        return order;
    }
    const files = await fetchOrderFiles(orderId);
    return {
        ...order,
        FILES: files,
    };
};

export const resolveProductsForSets = async (
    orderProductSets: NonNullable<OrderType['ORDER_PRODUCT_SETS']>
): Promise<OrderProductSetWithProductType[]> => {
    const products = await getProductsByIds(
        orderProductSets.map((set) => set.ID_PRODUCT).filter((id): id is number => !!id)
    );
    return orderProductSets.map((orderProductSet) => {
        const setProduct = products.find((product) => product.ID === orderProductSet.ID_PRODUCT);
        return {
            ...orderProductSet,
            NAME: setProduct?.NAME,
            SKU: setProduct?.SKU,
            ...flattenProductCatalogFields(setProduct),
        };
    });
};

/**
 * Merge server order into local edit state after a full refetch (e.g. reorder shipping).
 * Preserves already-loaded lazy collections when the refetch excluded them.
 */
export const mergeOrderDetailAfterServerRefresh = (
    previous: OrderType,
    incoming: OrderType
): OrderType => ({
    ...incoming,
    ORDER_PRODUCTS:
        incoming.ORDER_PRODUCTS !== undefined ? incoming.ORDER_PRODUCTS : previous.ORDER_PRODUCTS,
    ORDER_PRODUCT_SETS:
        incoming.ORDER_PRODUCT_SETS !== undefined
            ? incoming.ORDER_PRODUCT_SETS
            : previous.ORDER_PRODUCT_SETS,
    FILES: incoming.FILES !== undefined ? incoming.FILES : previous.FILES,
});

/**
 * Apply lazy-loaded fragments (products / files) without overwriting other edit fields.
 */
export const mergeLazyOrderDetailFields = (previous: OrderType, incoming: OrderType): OrderType => ({
    ...previous,
    ...(incoming.ORDER_PRODUCTS !== undefined ? { ORDER_PRODUCTS: incoming.ORDER_PRODUCTS } : {}),
    ...(incoming.ORDER_PRODUCT_SETS !== undefined
        ? { ORDER_PRODUCT_SETS: incoming.ORDER_PRODUCT_SETS }
        : {}),
    ...(incoming.FILES !== undefined ? { FILES: incoming.FILES } : {}),
});

/**
 * Build PUT/POST payload for order save.
 * Omits ORDER_PRODUCTS / FILES when they were not loaded yet (undefined),
 * so core update does not treat missing collections as empty arrays.
 */
export const buildOrderSavePayload = (
    editData: OrderType,
    options: {
        productsLoaded: boolean;
        filesLoaded: boolean;
        sameRecipientBilling?: boolean;
    }
): OrderType => {
    const { ORDER_PRODUCTS, FILES, ORDER_PALLETS, ...rest } = editData;
    let payload: OrderType = { ...rest };

    if (options.productsLoaded && ORDER_PRODUCTS !== undefined) {
        payload.ORDER_PRODUCTS = ORDER_PRODUCTS.map((product) => {
            const newProduct = { ...product };
            if ((newProduct.ORDERS_PRODUCTS_ID || 0) < 0) {
                delete newProduct.ORDERS_PRODUCTS_ID;
            }
            return newProduct;
        });
    }

    if (options.filesLoaded && FILES !== undefined) {
        payload.FILES = FILES;
    }

    if (ORDER_PALLETS !== undefined) {
        payload.ORDER_PALLETS = ORDER_PALLETS.map((pallet) => {
            const newPallet = { ...pallet };
            if ((newPallet.ID || 0) < 0) {
                delete newPallet.ID;
            }
            return newPallet;
        });
    }

    if (options.sameRecipientBilling) {
        payload = { ...payload, ...setSameDataRecipientBilling(editData) };
    }

    return payload;
};

export const renderTrackingNumber = (
    row: OrderType,
    trackingField: keyof OrderType & 'TRACKING_NUMBER' | 'RETURN_TRACKING_NUMBER' | 'EXCHANGE_TRACKING_NUMBER',
    icon?: ReactNode,
    clickHandle?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void
): ReactNode => {
    const trackingNumber = row[trackingField];
    if (!trackingNumber) {
        return '';
    }
    if (row.STATUS === 'productsNotAvailable') {
        return (<span></span>);
    }

    if (!checkPermission('ORDER.ACTION.SHIPPING.LABEL.SHOW')) {
        return trackingNumber;
    }

    if (row.ORDER_TYPE === 'b2c') {
        return (
            <TableLink
                href={getTrackingUrl(row.SHIPPER || '', trackingNumber)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={clickHandle}
            >
                {icon || trackingNumber}
            </TableLink>
        );
    }

    return (
        <>
            {trackingNumber.split(',').map((item, key) => {
                return (
                    <div>
                        <TableLink
                            key={String(key)}
                            href={getTrackingUrl(row.SHIPPER || '', item)}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {item}
                        </TableLink>
                    </div>
                );
            })}
        </>
    );
};

export const isDisableSplit = (order: OrderType | null): boolean => {
    if (!order?.ORDER_PRODUCTS) {
        return true;
    }

    let miss = 0;
    let total = 0;
    order.ORDER_PRODUCTS.filter((product) => product.LINE_STATE === 'E').forEach((product) => {
        total += Number(product.COUNT || 0);
        miss += Number(product.MISSING_COUNT || 0);
    });
    return miss === total;
};

const copyOrderToggleFields = (order: OrderType): Pick<
    OrderType,
    'ALLOW_EXPEDITION' | 'PRINT_FILES' | 'PRINT_RETURN_LABEL' | 'HOLD_BY_QUALITY' | 'PICK_FROM_CUSTOMS_WAREHOUSE'
> => ({
    ALLOW_EXPEDITION: order.ALLOW_EXPEDITION,
    PRINT_FILES: order.PRINT_FILES,
    PRINT_RETURN_LABEL: order.PRINT_RETURN_LABEL,
    HOLD_BY_QUALITY: order.HOLD_BY_QUALITY,
    PICK_FROM_CUSTOMS_WAREHOUSE: order.PICK_FROM_CUSTOMS_WAREHOUSE,
});

export const getSplitOrder = (order: OrderType): { updateOrder: OrderType; cloneOrder: OrderType } => {
    const productsClone: OrderProductType[] = [];
    const productsUpdate: OrderProductType[] = [];

    order.ORDER_PRODUCTS?.filter((product): boolean => product.LINE_STATE !== 'D').forEach((item) => {
        const MISSING_COUNT = Number(item.MISSING_COUNT);
        if (MISSING_COUNT) {
            const COUNT = Number(item.COUNT);
            productsClone.push({
                COUNT: item.MISSING_COUNT,
                LINE_STATE: item.LINE_STATE,
                PRODUCT_ID: item.PRODUCT_ID,
                // PRODUCT_CURRENCY: item.PRODUCT_CURRENCY,
                PRODUCT_PRICE: item.PRODUCT_PRICE,
                ID_SET: item.ID_SET,
                EXTEND_PRODUCT_TYPE: item.EXTEND_PRODUCT_TYPE,
                LOT: item.LOT,
                CUSTOMIZATION_TYPE: item.CUSTOMIZATION_TYPE,
                CUSTOMIZATION_TEXT: item.CUSTOMIZATION_TEXT,
                FILES: item.ORDER_PRODUCT_FILES?.map((prodFile: any) => ({
                    id: prodFile.FILE_ID,
                    type: prodFile.FILE.TYPE,
                    fileType: prodFile.FILE.FILE_TYPE,
                    fileName: prodFile.FILE.ORIGINAL_FILE_NAME,
                    fileSize: prodFile.FILE.FILE_SIZE,
                    createdAt: prodFile.FILE.CREATED_AT ?? prodFile.FILE.createdAt ?? null,
                    creatorRole: prodFile.FILE.CREATOR_ROLE ?? prodFile.FILE.creatorRole ?? null,
                })),
            });
            if (COUNT !== MISSING_COUNT) {
                productsUpdate.push({
                    ...item,
                    COUNT: (item.COUNT || 0) - (item.MISSING_COUNT || 0),
                    MISSING_COUNT: 0,
                });
            } else { // Delete if count and missing count are equal
                productsUpdate.push({
                    ...item,
                    LINE_STATE: 'D',
                });
            }
        } else {
            productsUpdate.push(item);
        }
    });
    const cloneOrder: Partial<OrderType> = {
        RECIPIENT_NAME: order.RECIPIENT_NAME,
        RECIPIENT_ATTENTION_NAME: order.RECIPIENT_ATTENTION_NAME,
        RECIPIENT_EMAIL: order.RECIPIENT_EMAIL,
        RECIPIENT_PHONE: order.RECIPIENT_PHONE,
        RECIPIENT_COMPANY_NAME: order.RECIPIENT_COMPANY_NAME,
        RECIPIENT_COMPANY_ID: order.RECIPIENT_COMPANY_ID,
        RECIPIENT_TAX_ID: order.RECIPIENT_TAX_ID,
        RECIPIENT_STREET: order.RECIPIENT_STREET,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO: order.RECIPIENT_ADDRESS_ADDITIONAL_INFO,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO_2: order.RECIPIENT_ADDRESS_ADDITIONAL_INFO_2,
        RECIPIENT_CITY: order.RECIPIENT_CITY,
        RECIPIENT_STATE: order.RECIPIENT_STATE,
        RECIPIENT_PROVINCE_CODE: order.RECIPIENT_PROVINCE_CODE,
        RECIPIENT_POST_CODE: order.RECIPIENT_POST_CODE,
        BILLING_NAME: order.BILLING_NAME,
        BILLING_ATTENTION_NAME: order.BILLING_ATTENTION_NAME,
        BILLING_EMAIL: order.BILLING_EMAIL,
        BILLING_PHONE: order.BILLING_PHONE,
        BILLING_COMPANY_NAME: order.BILLING_COMPANY_NAME,
        BILLING_COMPANY_ID: order.BILLING_COMPANY_ID,
        BILLING_TAX_ID: order.BILLING_TAX_ID,
        BILLING_STREET: order.BILLING_STREET,
        BILLING_ADDRESS_ADDITIONAL_INFO: order.BILLING_ADDRESS_ADDITIONAL_INFO,
        BILLING_ADDRESS_ADDITIONAL_INFO_2: order.BILLING_ADDRESS_ADDITIONAL_INFO_2,
        BILLING_CITY: order.BILLING_CITY,
        BILLING_STATE: order.BILLING_STATE,
        BILLING_PROVINCE_CODE: order.BILLING_PROVINCE_CODE,
        BILLING_POST_CODE: order.BILLING_POST_CODE,
        PARENT_ORDER_ID: order.ID,
        PARENT_ESHOP_ORDER_ID: order.ESHOP_ORDER_ID,
        METHOD: order.METHOD,
        INVOICE_DATE: order.INVOICE_DATE,
        SHIPPER: order.SHIPPER,
        PRICE: order.PRICE,
        SHIPPING_PRICE: order.SHIPPING_PRICE,
        CURRENCY: order.CURRENCY,
        // SHIPPING_CURRENCY: order.SHIPPING_CURRENCY,
        PAID: order.PAID,
        PAYMENT_METHOD: order.PAYMENT_METHOD,
        SHIPPER_SERVICE_CODE: order.SHIPPER_SERVICE_CODE,
        COD_PRICE: order.COD_PRICE,
        COD_CURRENCY: order.COD_CURRENCY,
        COD_REF_NUMBER: order.COD_REF_NUMBER,
        MESSAGE: order.MESSAGE,
        INVOICE_NUMBER: order.INVOICE_NUMBER,
        ADDRESS_ID: order.ADDRESS_ID,
        BRANCH_ID: order.BRANCH_ID,
        ORDER_TYPE: order.ORDER_TYPE,
        TRANSPORT_PAID_BY_SKLADON: 0,
        ORDER_BOX_COUNT: 1,
        ORDER_PRODUCTS: productsClone,
        FILES: (order.FILES || []).filter((file) => !SHIPPER_FILE_TYPES.includes(file.type || '')),
        SOURCE_SYS: SOURCE_SYS_MYSKLADON,
        STATUS: 'productsNotAvailable',
        ...copyOrderToggleFields(order),
    };

    const updateOrder = { ...order, ORDER_PRODUCTS: productsUpdate, STATUS: 'preReceived' };

    return { updateOrder, cloneOrder };
};

/**
 * Shared clone logic: groups child lines per set (sets with any deleted child line are dropped)
 * and marks which sets keep their child lines (customized sets).
 */
const getCloneSetsState = (data: OrderType, productsForSets: OrderProductSetWithProductType[]) => {
    const { mapProductsInSet, prices } = getProductSetData(data.ORDER_PRODUCTS || [], productsForSets);
    Object.keys(mapProductsInSet).forEach((idSet) => {
        if (mapProductsInSet[idSet].find((product) => product.LINE_STATE === 'D')) {
            delete mapProductsInSet[idSet];
        }
    });
    const isCustomized = (productsForSet: OrderProductSetWithProductType): boolean =>
        !!mapProductsInSet[String(productsForSet.ID_SET)]?.find((product) => product.CUSTOMIZATION_TYPE);
    return { mapProductsInSet, prices, isCustomized };
};

/** Sets whose child lines are copied into the clone (customized, not deleted); they render nested under the set row. */
export const getCloneProductsForSets = (
    data: OrderType,
    productsForSets: OrderProductSetWithProductType[]
): OrderProductSetWithProductType[] => {
    const { isCustomized } = getCloneSetsState(data, productsForSets);
    return productsForSets.filter(isCustomized);
};

export const getCloneOrder = async (data: OrderType, productsForSets: OrderProductSetWithProductType[]) => {
    const { mapProductsInSet, prices, isCustomized } = getCloneSetsState(data, productsForSets);

    const orderProducts: OrderProductType[] = data.ORDER_PRODUCTS?.filter((orderProduct) => !orderProduct.ID_SET) || [];

    const setProducts: OrderProductType[] = productsForSets.reduce<OrderProductType[]>((acc, productsForSet, key) => {
        const products = mapProductsInSet[String(productsForSet.ID_SET)];
        if (isCustomized(productsForSet)) {
            orderProducts.push(...products);
        } else {
            acc.push({
                PRODUCT_ID: productsForSet.ID_PRODUCT,
                COUNT: productsForSet.COUNT,
                CUSTOMS_PRODUCT_TYPE: products?.[0]?.CUSTOMS_PRODUCT_TYPE,
                PRODUCT_PRICE: prices[String(productsForSet.ID_SET)],
                LINE_STATE: products?.find((product) => product.LINE_STATE !== 'D') ? 'E' : 'D',
                ORDERS_PRODUCTS_ID: -1 - key - (data.ORDER_PRODUCTS?.length || 1),
                // Same shape as a set picked in NewProductBlock: full catalog data in nested PRODUCT.
                PRODUCT: {
                    ...flattenProductCatalogFields({ ...productsForSet, ID: undefined }),
                    ID: productsForSet.ID_PRODUCT,
                    NAME: productsForSet.NAME,
                    SKU: productsForSet.SKU,
                } as OrderProductType['PRODUCT'],
            });
        }
        return acc;
    }, []).filter((item) => item && item?.LINE_STATE === 'E');

    return {
        RECIPIENT_NAME: data.RECIPIENT_NAME,
        RECIPIENT_ATTENTION_NAME: data.RECIPIENT_ATTENTION_NAME,
        RECIPIENT_EMAIL: data.RECIPIENT_EMAIL,
        RECIPIENT_PHONE: data.RECIPIENT_PHONE,
        RECIPIENT_COMPANY_NAME: data.RECIPIENT_COMPANY_NAME,
        RECIPIENT_COMPANY_ID: data.RECIPIENT_COMPANY_ID,
        RECIPIENT_TAX_ID: data.RECIPIENT_TAX_ID,
        RECIPIENT_STREET: data.RECIPIENT_STREET,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO: data.RECIPIENT_ADDRESS_ADDITIONAL_INFO,
        RECIPIENT_ADDRESS_ADDITIONAL_INFO_2: data.RECIPIENT_ADDRESS_ADDITIONAL_INFO_2,
        RECIPIENT_CITY: data.RECIPIENT_CITY,
        RECIPIENT_STATE: data.RECIPIENT_STATE,
        RECIPIENT_PROVINCE_CODE: data.RECIPIENT_PROVINCE_CODE,
        RECIPIENT_POST_CODE: data.RECIPIENT_POST_CODE,
        RECIPIENT_GLN: data.RECIPIENT_GLN,
        BILLING_NAME: data.BILLING_NAME,
        BILLING_ATTENTION_NAME: data.BILLING_ATTENTION_NAME,
        BILLING_EMAIL: data.BILLING_EMAIL,
        BILLING_PHONE: data.BILLING_PHONE,
        BILLING_COMPANY_NAME: data.BILLING_COMPANY_NAME,
        BILLING_COMPANY_ID: data.BILLING_COMPANY_ID,
        BILLING_TAX_ID: data.BILLING_TAX_ID,
        BILLING_STREET: data.BILLING_STREET,
        BILLING_ADDRESS_ADDITIONAL_INFO: data.BILLING_ADDRESS_ADDITIONAL_INFO,
        BILLING_ADDRESS_ADDITIONAL_INFO_2: data.BILLING_ADDRESS_ADDITIONAL_INFO_2,
        BILLING_CITY: data.BILLING_CITY,
        BILLING_STATE: data.BILLING_STATE,
        BILLING_PROVINCE_CODE: data.BILLING_PROVINCE_CODE,
        BILLING_POST_CODE: data.BILLING_POST_CODE,
        BILLING_GLN: data.BILLING_GLN,
        PARENT_ORDER_ID: data.ID,
        PARENT_ESHOP_ORDER_ID: data.ESHOP_ORDER_ID,
        METHOD: data.METHOD || 'DEL',
        INVOICE_DATE: data.INVOICE_DATE,
        SHIPPER: data.SHIPPER,
        PRICE: data.PRICE,
        SHIPPING_PRICE: data.SHIPPING_PRICE,
        CURRENCY: data.CURRENCY,
        // SHIPPING_CURRENCY: data.SHIPPING_CURRENCY,
        PAID: data.PAID,
        PAYMENT_METHOD: data.PAYMENT_METHOD,
        SHIPPER_SERVICE_CODE: data.SHIPPER_SERVICE_CODE,
        COD_PRICE: data.COD_PRICE,
        COD_CURRENCY: data.COD_CURRENCY,
        COD_REF_NUMBER: data.COD_REF_NUMBER,
        MESSAGE: data.MESSAGE,
        INVOICE_NUMBER: data.INVOICE_NUMBER,
        ADDRESS_ID: data.ADDRESS_ID,
        BRANCH_ID: data.BRANCH_ID,
        ORDER_TYPE: data.ORDER_TYPE,
        STATUS: 'preReceived',
        SOURCE_SYS: SOURCE_SYS_MYSKLADON,
        TRANSPORT_PAID_BY_SKLADON: 0,
        ORDER_BOX_COUNT: 1,
        ORDER_PRODUCTS: [
            ...orderProducts.filter(
                (product: any): boolean =>
                    product.LINE_STATE !== 'D')
            .map((item: any, key: any): any => ({
                COUNT: item.COUNT,
                LINE_STATE: item.LINE_STATE,
                ORDERS_PRODUCTS_ID: -1 - key,
                PRODUCT: item.PRODUCT,
                PRODUCT_ID: item.PRODUCT_ID,
                // PRODUCT_CURRENCY: item.PRODUCT_CURRENCY,
                PRODUCT_PRICE: item.PRODUCT_PRICE,
                EXTEND_PRODUCT_TYPE: item.EXTEND_PRODUCT_TYPE,
                CUSTOMS_PRODUCT_TYPE: item.CUSTOMS_PRODUCT_TYPE,
                LOT: item.LOT,
                CUSTOMIZATION_TYPE: item.CUSTOMIZATION_TYPE,
                CUSTOMIZATION_TEXT: item.CUSTOMIZATION_TEXT,
                ID_SET: item.ID_SET,
                FILES: item.ORDER_PRODUCT_FILES?.map((prodFile: any) => ({
                    id: prodFile.FILE_ID,
                    type: prodFile.FILE.TYPE,
                    fileType: prodFile.FILE.FILE_TYPE,
                    fileName: prodFile.FILE.ORIGINAL_FILE_NAME,
                    fileSize: prodFile.FILE.FILE_SIZE,
                    createdAt: prodFile.FILE.CREATED_AT ?? prodFile.FILE.createdAt ?? null,
                    creatorRole: prodFile.FILE.CREATOR_ROLE ?? prodFile.FILE.creatorRole ?? null,
                })),
            })),
            ...setProducts
        ],
        FILES: (data.FILES || []).filter((file) => !SHIPPER_FILE_TYPES.includes(file.type || '')),
        ...copyOrderToggleFields(data),
    };
};

export const getChangesProviderCompanies = (data: any, type: 'recipient' | 'billing') => {
    const prefix = type === 'billing' ? 'BILLING' : 'RECIPIENT';
    const arrChanges = [
        ['NAME', 'RECIPIENT_NAME'],
        ['ATTENTION_NAME', 'CONTACT_PERSON'],
        ['COMPANY_NAME', 'COMPANY_NAME'],
        ['GLN', 'GLN'],
        ['EMAIL', 'EMAIL'],
        ['PHONE', 'PHONE'],
        ['COMPANY_ID', 'COMPANY_ID'],
        ['STREET', 'STREET'],
        ['CITY', 'CITY'],
        ['POST_CODE', 'ZIP_CODE'],
        ['STATE', 'STATE'],
        ['ADDRESS_ADDITIONAL_INFO', 'ADDRESS_ADDITIONAL_INFO'],
        ['PROVINCE_CODE', 'PROVINCE_CODE'],
    ];
    const values: OrderType = {};
    arrChanges.forEach((keys) => {
        values[`${prefix}_${keys[0]}`] = data[keys[1]];
    });
    return values;
};

export const getPaidData = (
    paidOption: PaidActionOptionType,
    dataSrc: OrderType,
    confirmData?: { data: OrderType }
) => {
    const dataSave = { ...dataSrc };
    dataSave.STATUS = paidOption.STATUS;
    dataSave.PAYMENT_METHOD = paidOption.PAYMENT_METHOD;
    dataSave.PAID = paidOption.PAID;

    if (confirmData && paidOption.label === 'cashOnDelivery') {
        dataSave.COD_PRICE = confirmData.data.COD_PRICE;
        dataSave.COD_CURRENCY = confirmData.data.COD_CURRENCY;
        dataSave.COD_REF_NUMBER = confirmData.data.COD_REF_NUMBER;
    }

    return dataSave;
};

export const getChangelogStatuses = (changelog?: OrderChangelogType | null) => {
    return (
        changelog?.updates
            .filter((update) => update.data?.STATUS?.hasOwnProperty('new'))
            // @ts-ignore
            .map((update) => update.data?.STATUS.new)
    );
};

type IconTypeType = 'ok' | 'error' | 'warning' | 'okWarning' | 'success' | 'successWarning';

export const isAtPickupPointStepVisible = (
    order: Pick<OrderType, 'SHIPPER_SERVICE_CODE' | 'AT_PICKUP_POINT_AT'>
): boolean =>
    Boolean(order.AT_PICKUP_POINT_AT) ||
    PICKUP_POINT_SERVICE_CODES.includes(order.SHIPPER_SERVICE_CODE ?? '');

export const getStatusIconType = (
    status?: string,
    changelog?: OrderChangelogType | null
): {
    received?: IconTypeType;
    processing?: IconTypeType;
    waitingForCarrier?: IconTypeType;
    onTheWay?: IconTypeType;
    atPickupPoint?: IconTypeType;
    delivered?: IconTypeType;
    messageProcessing?: string;
} => {
    let statuses: any[] | undefined = [];
    let messageProcessing: string | undefined = undefined;
    switch (status) {
        case 'received':
            return { received: 'success' };

        case 'onTheWay':
            statuses = getChangelogStatuses(changelog);

            if (statuses?.includes('incorrect')) {
                messageProcessing = translate('orderDetail.lateBecouseIncorrect');
            }
            if (statuses?.includes('productsNotAvailable')) {
                messageProcessing = translate('orderDetail.lateBecousePNA');
            }
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'success',
                messageProcessing,
            };

        case 'atPickupPoint':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'ok',
                atPickupPoint: 'success',
            };

        case 'inProcess':
            return {
                received: 'ok',
                processing: 'success',
            };

        case 'delivered':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'ok',
                atPickupPoint: 'ok',
                delivered: 'success',
            };

        case 'lost':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'ok',
                atPickupPoint: 'ok',
                delivered: 'error',
            };

        case 'returningBack':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'ok',
                atPickupPoint: 'ok',
                delivered: 'warning',
            };
        case 'receivedBack':
        case 'returnedBack':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'ok',
                onTheWay: 'ok',
                atPickupPoint: 'ok',
                delivered: 'successWarning',
            };

        case 'incorrect':
            return {
                received: 'error',
            };

        case 'productsNotAvailable':
            return {
                received: 'ok',
                processing: 'warning',
            };

        case 'packed':
            return {
                received: 'ok',
                processing: 'ok',
            };

        case 'waitingForCarrier':
            return {
                received: 'ok',
                processing: 'ok',
                waitingForCarrier: 'success',
            };

        case 'waitingForPayment':
            return {
                received: 'warning',
            };
        case 'waitingForShipment':
            return {
                received: 'ok',
            };

        case 'suspended':
            if (getChangelogStatuses(changelog)?.includes('inProcess')) {
                return {
                    received: 'ok',
                    processing: 'warning',
                };
            }
            return { received: 'warning' };

        case 'canceled':
            statuses = getChangelogStatuses(changelog);
            if (
                ['lost', 'returningBack', 'returnedBack', 'delivered'].find((item) =>
                    statuses?.includes(item)
                )
            ) {
                return {
                    received: 'ok',
                    processing: 'ok',
                    waitingForCarrier: 'ok',
                    onTheWay: 'ok',
                    delivered: 'okWarning',
                };
            }
            if (statuses?.includes('onTheWay')) {
                return {
                    received: 'ok',
                    processing: 'ok',
                    waitingForCarrier: 'ok',
                    onTheWay: 'okWarning',
                };
            }
            if (statuses?.includes('waitingForCarrier')) {
                return {
                    received: 'ok',
                    processing: 'ok',
                    waitingForCarrier: 'okWarning',
                };
            }
            if (['inProcess', 'packed'].find((item) => statuses?.includes(item))) {
                return {
                    received: 'ok',
                    processing: 'okWarning',
                };
            }
            return { received: 'okWarning' };
        default:
            return {};
    }
};

export const getStatusIconComponent = (step?: string, type?: string) => {
    switch (step) {
        case 'received':
            switch (type) {
                case 'ok':
                    return <ReceivedOkBigIcon />;
                case 'okWarning':
                    return <ReceivedOkWithWarningBigIcon />;
                case 'error':
                    return <ReceivedErrorBigIcon />;
                case 'warning':
                    return <ReceivedWarningBigIcon />;
                case 'success':
                    return <ReceivedSuccessBigIcon />;
                default:
                    return <ReceivedDisabledBigIcon />;
            }
        case 'processing':
            switch (type) {
                case 'ok':
                    return <ProcessingOkBigIcon />;
                case 'okWarning':
                    return <ProcessingOkWithWarningBigIcon />;
                case 'error':
                    return <ProcessingErrorBigIcon />;
                case 'warning':
                    return <ProcessingWarningBigIcon />;
                case 'success':
                    return <ProcessingSuccessBigIcon />;
                default:
                    return <ProcessingDisabledBigIcon />;
            }
        case 'waitingForCarrier':
            switch (type) {
                case 'ok':
                    return <WaitingForCarrierOkBigIcon />;
                case 'okWarning':
                    return <WaitingForCarrierOkWithWarningBigIcon />;
                case 'error':
                    return <WaitingForCarrierErrorBigIcon />;
                case 'warning':
                    return <WaitingForCarrierWarningBigIcon />;
                case 'success':
                    return <WaitingForCarrierSuccessBigIcon />;
                default:
                    return <WaitingForCarrierDisabledBigIcon />;
            }
        case 'onTheWay':
            switch (type) {
                case 'ok':
                    return <OnTheWayOkBigIcon />;
                case 'okWarning':
                    return <OnTheWayOkWithWarningBigIcon />;
                case 'error':
                    return <OnTheWayErrorBigIcon />;
                case 'warning':
                    return <OnTheWayWarningBigIcon />;
                case 'success':
                    return <OnTheWaySuccessBigIcon />;
                default:
                    return <OnTheWayDisabledBigIcon />;
            }
        case 'atPickupPoint':
            switch (type) {
                case 'ok':
                    return <AtPickupPointOkBigIcon />;
                case 'okWarning':
                    return <AtPickupPointOkWithWarningBigIcon />;
                case 'error':
                    return <AtPickupPointErrorBigIcon />;
                case 'warning':
                    return <AtPickupPointWarningBigIcon />;
                case 'success':
                    return <AtPickupPointSuccessBigIcon />;
                default:
                    return <AtPickupPointDisabledBigIcon />;
            }
        case 'delivered':
        default:
            switch (type) {
                case 'ok':
                    return <DeliveredOkBigIcon />;
                case 'okWarning':
                    return <DeliveredOkWithWarningBigIcon />;
                case 'error':
                    return <DeliveredErrorBigIcon />;
                case 'warning':
                    return <DeliveredWarningBigIcon />;
                case 'success':
                    return <DeliveredSuccessBigIcon />;
                case 'successWarning':
                    return <DeliveredSuccessWithWarningBigIcon />;
                default:
                    return <DeliveredDisabledBigIcon />;
            }
    }
};

export const getStatusIcon = (status: string) => {
    switch (status) {
        case 'received':
            return getStatusIconComponent('received', 'success');
        case 'onTheWay':
            return getStatusIconComponent('onTheWay', 'success');
        case 'atPickupPoint':
            return getStatusIconComponent('atPickupPoint', 'success');
        case 'inProcess':
            return getStatusIconComponent('processing', 'success');
        case 'delivered':
            return getStatusIconComponent('delivered', 'success');
        case 'lost':
            return getStatusIconComponent('delivered', 'error');
        case 'returningBack':
        case 'receivedBack':
        case 'returnedBack':
            return getStatusIconComponent('delivered', 'warning');
        case 'incorrect':
            return getStatusIconComponent('received', 'error');
        case 'productsNotAvailable':
            return getStatusIconComponent('processing', 'warning');
        case 'packed':
            return getStatusIconComponent('processing', 'ok');
        case 'waitingForCarrier':
            return getStatusIconComponent('waitingForCarrier', 'success');
        case 'waitingForPayment':
            return getStatusIconComponent('received', 'warning');
        case 'waitingForShipment':
            return getStatusIconComponent('received', 'ok');
        case 'suspended':
            return getStatusIconComponent('processing', 'warning');
        case 'canceled':
            return <CanceledIcon />;
        default:
            return getStatusIconComponent('received');
    }
};

export const formatIncorrectMessage = (data: OrderType, shipperMessageCodes: ConstantsCodeType[]): string => {
    const langkey = getLangKey();
    let message = (data.INCORRECT_ORDER_MESSAGE || '').replace(/,/g, ', ');
    if (message) {
        shipperMessageCodes
            .filter((item: any): boolean => item.shipperCode === data.SHIPPER && item.LANG === langkey)
            .forEach((item: any) => {
                message = message.replaceAll(item.value, item.label);
            });
    }
    return message;
};

export const getProductsByIds = (ids: number[]): Promise<ProductType[]> => {
    return new Promise((resolve): void => {
        if (!ids.length) {
            resolve([]);
        } else {
            const url = `api/secure/products?ids=${ids.join(',')}`;
            api('get', url).then((response) => {
                const body = response.data;
                if (Array.isArray(body)) {
                    resolve(body as ProductType[]);
                    return;
                }
                if (body && Array.isArray(body.data)) {
                    resolve(body.data as ProductType[]);
                    return;
                }
                resolve([]);
            }).catch(() => {
                resolve([]);
            });
        }
    });
};

export type ProductCountInputValue = ProductQuantityInputValue;

export {
    isEmptyProductQuantityInput as isEmptyProductCountInput,
    parseProductQuantityValue as parseProductCountValue,
    toProductQuantityInputValue as toProductCountInputValue,
} from 'src/utils/productQuantityInput';

export const changeOrderProductCount = (
    products: OrderProductType[],
    ordersProductsId: number | string,
    newCount: number
): OrderProductType[] => {
    if (newCount <= 0) {
        return products;
    }
    return products.map((product) => {
        if (String(product.ORDERS_PRODUCTS_ID) !== String(ordersProductsId)) {
            return product;
        }
        return { ...product, COUNT: newCount, LINE_STATE: 'E' };
    });
};

export const finalizeOrderProductCountOnBlur = (
    products: OrderProductType[],
    ordersProductsId: number | string,
    lastValue: ProductCountInputValue,
    countOnFocus: number
): OrderProductType[] => {
    return products.map((product) => {
        if (String(product.ORDERS_PRODUCTS_ID) !== String(ordersProductsId)) {
            return product;
        }
        if (shouldRestoreProductQuantityOnBlur(lastValue)) {
            return { ...product, COUNT: countOnFocus, LINE_STATE: 'E' };
        }
        return product;
    });
};

export const deactivateOrderSet = (
    products: OrderProductType[],
    productsForSets: OrderProductSetWithProductType[],
    idSet: number,
    restoreSetCount: number
): { products: OrderProductType[]; productsForSets: OrderProductSetWithProductType[] } => {
    const updatedProductsForSets = productsForSets.map((set) => {
        if (set.ID_SET !== idSet) {
            return set;
        }
        return { ...set, COUNT: restoreSetCount, LINE_STATE: 'D' };
    });

    const updatedProducts = products.map((product) => {
        if (Number(product.ID_SET) !== idSet) {
            return product;
        }
        return { ...product, LINE_STATE: 'D' };
    });

    return { products: updatedProducts, productsForSets: updatedProductsForSets };
};

export const changeOrderSetCount = (
    products: OrderProductType[],
    productsForSets: OrderProductSetWithProductType[],
    idSet: number,
    newSetCount: number
): { products: OrderProductType[]; productsForSets: OrderProductSetWithProductType[] } => {
    if (newSetCount <= 0) {
        return { products, productsForSets };
    }

    const setEntry = productsForSets.find((set) => set.ID_SET === idSet);
    const oldSetCount = Number(setEntry?.COUNT || 0);

    const updatedProductsForSets = productsForSets.map((set) => {
        if (set.ID_SET !== idSet) {
            return set;
        }
        return { ...set, COUNT: newSetCount, LINE_STATE: 'E' };
    });

    const updatedProducts = products.map((product) => {
        if (Number(product.ID_SET) !== idSet) {
            return product;
        }
        if (product.LINE_STATE === 'D') {
            return product;
        }
        if (!oldSetCount) {
            return product;
        }
        const newChildCount = Math.round((Number(product.COUNT || 0) / oldSetCount) * newSetCount);
        return { ...product, COUNT: newChildCount, LINE_STATE: 'E' };
    });

    return { products: updatedProducts, productsForSets: updatedProductsForSets };
};

export const finalizeOrderSetCountOnBlur = (
    products: OrderProductType[],
    productsForSets: OrderProductSetWithProductType[],
    idSet: number,
    lastValue: ProductCountInputValue,
    setCountOnFocus: number
): { products: OrderProductType[]; productsForSets: OrderProductSetWithProductType[] } => {
    if (lastValue === 'empty' || lastValue === 'invalid' || lastValue === 0) {
        return {
            products,
            productsForSets: productsForSets.map((set) => {
                if (set.ID_SET !== idSet) {
                    return set;
                }
                return { ...set, COUNT: setCountOnFocus, LINE_STATE: 'E' };
            }),
        };
    }
    return { products, productsForSets };
};

export const getProductSetData = (data: OrderProductType[], productsForSets: OrderProductSetWithProductType[]) => {
    const mapProductsInSet = groupBy(
        data
            ?.filter((product) => !!product.ID_SET)
            .map((product) => ({
                ...product,
                NAME: getOrderProductDisplayName(product),
                SKU: getOrderProductDisplaySku(product)
            })) || [],
        'ID_SET'
    );

    const prices: Record<string, number> = {};
    productsForSets?.map((productsForSet) => {
        const products = mapProductsInSet[String(productsForSet.ID_SET)] || [];
        prices[productsForSet.ID_SET] =
            sumArraySimple(
                products
                    .filter((product) => product.LINE_STATE !== 'D')
                    .map((product) => (product.COUNT || 0) * (product.PRODUCT_PRICE || 0))
            ) / productsForSet.COUNT;
    });

    return { mapProductsInSet, prices };
};

export const hasActiveFilters = (filters: GridFilterType[]): boolean =>
    filters.some((f) => (Array.isArray(f.value) ? f.value.length > 0 : !!f.value));

export const isSetRowVisibleForFilters = (
    setRow: TableRowType,
    filters: GridFilterType[],
    columns: TableColumnType[]
): boolean =>
    filteringRow(setRow, filters, columns) ||
    (setRow.products || []).some((p: TableRowType) => filteringRow(p, filters, columns));

export const getVisibleSetProducts = (
    products: TableRowType[],
    filters: GridFilterType[],
    columns: TableColumnType[]
): TableRowType[] =>
    hasActiveFilters(filters)
        ? products.filter((p) => filteringRow(p, filters, columns))
        : products;

const getText = (text?: string, options?: any) => {
    return new TextRun({
        text: text || '',
        break: 1,
        font: 'Calibri',
        size: 22,
        ...(options || {}),
    });
};

export const dowloadRecipientInfo = (data: OrderType | null) => {
    if (!data) {
        return;
    }

    const countires = getCountryOptions();
    const country = countires.find((item) => item.value === data.RECIPIENT_STATE)?.label;

    const document = new Document({
        sections: [
            {
                children: [
                    new Paragraph({
                        children: [
                            getText(`Dobrý den,`, { break: 0 }),
                            getText(
                                `prosím o stažení zásilky TN – ${
                                    data.TRACKING_NUMBER || ''
                                } a navrácení na adresu odesílatele.`,
                                { break: 2 }
                            ),
                            getText(`Příjemce:`, { bold: true, break: 2 }),
                            getText(data.RECIPIENT_NAME),
                            getText(data.RECIPIENT_STREET),
                            getText(data.RECIPIENT_CITY),
                            getText(data.RECIPIENT_POST_CODE),
                            getText(data.RECIPIENT_PHONE),
                            getText(data.RECIPIENT_EMAIL),
                            getText(country),
                            getText(`Odesílatel:`, { bold: true, break: 2 }),
                            getText(`Skladon`),
                            getText(`Průmyslová 377`),
                            getText(`Mošnov 742 51`),
                            getText(`Děkuji za vyřízení našeho požadavku.`, { break: 2 }),
                            getText(`S pozdravem`, { break: 2 }),
                        ],
                    }),
                ],
            },
        ],
    });

    Packer.toBlob(document).then((blob) => {
        saveAs(blob, 'Recipient info.docx');
    });
};

export interface OrderBoxCountEditPermissionsType {
    permissionEditAll: boolean;
    permissionManagerEdit: boolean;
    permissionEditOrderBoxCount: boolean;
    summary?: boolean;
}

/** ORDER_BOX_COUNT is editable only with EDITALL, MANAGER_EDIT or PROPERTIES.ORDER_BOX_COUNT.EDIT.<STATUS>. */
export const isOrderBoxCountEditDisabled = (permissions: OrderBoxCountEditPermissionsType): boolean =>
    !(permissions.permissionEditAll || permissions.permissionManagerEdit || permissions.permissionEditOrderBoxCount) ||
    !!permissions.summary;

function groupBy<T, K extends keyof T>(array: T[], key: K): Record<string, T[]> {
    return array.reduce((accumulator, currentItem) => {
        const keyValue = String(currentItem[key]);
        (accumulator[keyValue] = accumulator[keyValue] || []).push(currentItem);
        return accumulator;
    }, {} as Record<string, T[]>);
}

export function isCzechEaster(date: Date | Moment | string) {
    const year = moment(date).year();
    let f = Math.floor,
        // Golden Number - 1
        G = year % 19,
        C = f(year / 100),
        // related to Epact
        H = (C - f(C / 4) - f((8 * C + 13) / 25) + 19 * G + 15) % 30,
        // number of days from 21 March to the Paschal full moon
        I = H - f(H / 28) * (1 - f(29 / (H + 1)) * f((21 - G) / 11)),
        // weekday for the Paschal full moon
        J = (year + f(year / 4) + I + 2 - C + f(C / 4)) % 7,
        // number of days from 21 March to the Sunday on or before the Paschal full moon
        L = I - J,
        month = 3 + f((L + 40) / 44),
        day = L + 28 - 31 * f(month / 4);
    const format = 'D.M.Y';
    const easterMondayDate = moment(new Date(year, month - 1, day)).add(1, 'day').format(format);
    const easterFridayDate = moment(new Date(year, month - 1, day)).add(-2, 'days').format(format);
    return [easterMondayDate, easterFridayDate].includes(moment(date).format(format));
}
